import useSmoothProgress from "@hooks/useSmoothProgress";
import InfoIcon from "@mui/icons-material/Info";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Slider,
  Tooltip,
  Typography,
  styled,
  useTheme
} from "@mui/material";
import LyricToolbar from "@pages/LyricsView/LyricToolbar";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SpotifyWebApi from "spotify-web-api-js";
import SpotifyLogoRoundImage from "../img/spotify-logo-round.png";
import { requestPlaybackSync } from "../music/playbackSync";
import { CurrentlyPlayingState } from "../types/currentlyPlayingState";
import { IToken } from "../types/token";
import { formatMilliseconds, responseError } from "../utils";

const placeholder1PxImage =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mO8+x8AAr8B3gzOjaQAAAAASUVORK5CYII="; // Generated using https://png-pixel.com/

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.background.paper,
  top: "auto",
  bottom: 0,
  paddingTop: 6,
  paddingBottom: 6
}));

const MenuItemWrapper = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  "& .MuiToolbar-root": {
    minHeight: 0,
    padding: 0
  }
}));

const PlayerWrapper = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "grid",
  gap: "clamp(6px, 1.5vw, 16px)",
  gridTemplateColumns: "auto minmax(0, 1fr) auto",
  minWidth: 0,
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    gap: 4
  }
}));

const SongWrapper = styled(Box)(({ theme }) => ({
  display: "inline-grid",
  gridTemplateColumns: "auto 1fr",
  gridTemplateRows: "1fr 1fr",
  gridColumnGap: 5,
  maxWidth: 420,
  minWidth: 0,
  width: "100%"
}));

const SongAlbumArtWrapper = styled(Box)({
  gridColumnStart: 1,
  gridColumnEnd: 2,
  gridRowStart: 1,
  gridRowEnd: 3
});

const InfoBoxWrapper = styled(Box)(({ theme }) => ({
  width: 72,
  height: 56,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  color: theme.palette.text.primary,
  padding: theme.spacing(1.5),
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  justifySelf: "end",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  },
  [theme.breakpoints.down("sm")]: {
    height: 48,
    padding: theme.spacing(0.75),
    width: 58
  }
}));

const InfoContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8
});

const PopularityValue = styled(Typography)(({ theme }) => ({
  fontSize: "clamp(1rem, 2.2vw, 1.25rem)",
  fontWeight: "bold",
  color: theme.palette.text.primary
}));

const SongAlbumArt = styled("img")({
  height: 40,
  width: 40,
  borderRadius: 4
});

const SongDetail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: "clamp(0.78rem, 1.5vw, 0.95rem)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
}));

const TimeControl = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
}));

const ControlsWrapper = styled(Box)({
  display: "inline-grid",
  gridTemplateColumns: "auto auto auto",
  alignItems: "center",
  justifyContent: "center"
});

const Control = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  cursor: "pointer"
}));

const SliderWrapper = styled(Box)(({ theme }) => ({
  width: 300,
  display: "inline-flex",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
    width: "100%"
  }
}));

const SliderStyled = styled(Slider)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: "10px 0"
  },
  marginLeft: 10,
  marginRight: 10
}));

interface PlayerProps {
  currentlyPlayingSong: CurrentlyPlayingState;
  token: IToken | null;
}

const Player: React.FC<PlayerProps> = ({ currentlyPlayingSong, token }) => {
  let albumArt = currentlyPlayingSong.currentlyPlayingObject?.item?.album.images[0].url;
  let title = currentlyPlayingSong.currentlyPlayingObject?.item?.name ?? "---";
  let artist =
    currentlyPlayingSong.currentlyPlayingObject?.item?.artists.map(a => a.name).join(", ") ?? "---";
  let durationMs = currentlyPlayingSong.currentlyPlayingObject?.item?.duration_ms ?? 0;
  const progressMs = currentlyPlayingSong.currentlyPlayingObject?.progress_ms ?? 0;
  const isPlaying = currentlyPlayingSong.currentlyPlayingObject?.is_playing ?? false;

  const location = useLocation();

  const theme = useTheme();
  const navigate = useNavigate();

  let popularity = currentlyPlayingSong.currentlyPlayingObject?.item?.popularity ?? 0;

  if (currentlyPlayingSong?.currentlyPlayingObject?.currently_playing_type === "ad") {
    albumArt = SpotifyLogoRoundImage;
    title = "Advertisement";
    artist = "Spotify";
    durationMs = Math.max(progressMs, 30 * 1000); // Typically 30s but allow for something over
    popularity = 0;
  }

  const {
    onUserFinishedSliding,
    onUserSlide,
    progress: smoothedProgressMs
  } = useSmoothProgress(progressMs, durationMs, isPlaying, token);

  const onSkipPrevious = () => {
    if (token) {
      const spotifyApi = new SpotifyWebApi();
      spotifyApi.setAccessToken(token.value);
      spotifyApi
        .skipToPrevious()
        .then(requestPlaybackSync)
        .catch(e => responseError("Failed to Skip to Previous Song", e));
    }
  };
  const onSkipNext = () => {
    if (token) {
      const spotifyApi = new SpotifyWebApi();
      spotifyApi.setAccessToken(token.value);
      spotifyApi
        .skipToNext()
        .then(requestPlaybackSync)
        .catch(e => {
          responseError("Failed to Skip to Next Song", e);
        });
    }
  };
  const onPlayPauseToggle = () => {
    if (token) {
      const spotifyApi = new SpotifyWebApi();
      spotifyApi.setAccessToken(token.value);
      if (isPlaying) {
        spotifyApi
          .pause()
          .then(requestPlaybackSync)
          .catch(e => responseError("Failed to Pause", e));
      } else {
        spotifyApi
          .play()
          .then(requestPlaybackSync)
          .catch(e => responseError("Failed to Play", e));
      }
    }
  };

  const onInfoBoxClick = () => {
    // Navigate to curiosities page
    navigate("/curiosities");
  };

  if (!currentlyPlayingSong?.currentlyPlayingObject) {
    return null;
  }

  function toggleAlbum(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.preventDefault();
    if (location.pathname === "/album/") {
      navigate("/");
      return;
    }
    if (
      !currentlyPlayingSong.currentlyPlayingObject ||
      !currentlyPlayingSong.currentlyPlayingObject.item ||
      !currentlyPlayingSong.currentlyPlayingObject.item.album.id
    ) {
      return;
    }
    navigate("/album/");
  }

  return (
    <AppBarStyled>
      <Container maxWidth="lg">
        <PlayerWrapper>
          <MenuItemWrapper>
            <LyricToolbar setSyncEnabled={() => {}} />
          </MenuItemWrapper>

          <SongWrapper onClick={toggleAlbum}>
            <SongAlbumArtWrapper>
              <SongAlbumArt
                src={albumArt ?? placeholder1PxImage}
                alt={`Album art for current song`}
              />
            </SongAlbumArtWrapper>
            <SongDetail title={title}>{title}</SongDetail>
            <SongDetail title={artist}>{artist}</SongDetail>
          </SongWrapper>

          <InfoBoxWrapper onClick={onInfoBoxClick}>
            <InfoContent>
              <PopularityValue>{popularity}</PopularityValue>
              <InfoIcon fontSize="small" color="action" />
            </InfoContent>
            <Tooltip title="View Song Curiosities" placement="top">
              <Box />
            </Tooltip>
          </InfoBoxWrapper>
        </PlayerWrapper>
      </Container>
    </AppBarStyled>
  );
};

export default Player;
